/* © Facilogi. See COPYRIGHT file for full copyright & licensing details. */

import Feathers from "@feathersjs/feathers";

import { CustomError } from "@Internal/Models";
import { Autobind } from "@Internal/Utils";

import { ConsoleUsersService } from "App/Contracts";
import { User } from "App/Models";

import BaseService, { ProcessException } from "./Base";

/* List/detail come from the base "Internal/Users" service; every lifecycle action targets its own
   endpoint and is triggered with update( id ) the same way Coupons/Stop is. */
export default class ConsoleUsers extends BaseService<User> implements ConsoleUsersService {
    private inviteService: Feathers.Service<any>;
    private disableService: Feathers.Service<any>;
    private reactivateService: Feathers.Service<any>;
    private reinviteService: Feathers.Service<any>;
    private assignRoleService: Feathers.Service<any>;

    constructor(
        service: Feathers.Service<any>,
        inviteService: Feathers.Service<any>,
        disableService: Feathers.Service<any>,
        reactivateService: Feathers.Service<any>,
        reinviteService: Feathers.Service<any>,
        assignRoleService: Feathers.Service<any>
    ) {
        super( service );

        this.inviteService = inviteService;
        this.disableService = disableService;
        this.reactivateService = reactivateService;
        this.reinviteService = reinviteService;
        this.assignRoleService = assignRoleService;
    }

    @Autobind
    async Invite( user: User ): Promise<CustomError | undefined> {
        try {
            const results = await this.inviteService.create({
                FirstName: user.FirstName,
                LastName: user.LastName,
                Email: user.Email,
                Phone: user.Phone,
                RoleId: user.RoleId
            });

            if( results !== undefined && results.Error !== undefined )
                return new CustomError( results.Error.Id, results.Error.Message );

        } catch( error ) {
            return ProcessException( error );
        }

        return undefined;
    }

    @Autobind
    async Disable( id: number ): Promise<CustomError | undefined> {
        return this.action( this.disableService, id );
    }

    @Autobind
    async Reactivate( id: number ): Promise<CustomError | undefined> {
        return this.action( this.reactivateService, id );
    }

    @Autobind
    async Reinvite( id: number ): Promise<CustomError | undefined> {
        return this.action( this.reinviteService, id );
    }

    @Autobind
    async AssignRole( id: number, roleId: number ): Promise<CustomError | undefined> {
        return this.action( this.assignRoleService, id, { RoleId: roleId } );
    }

    private async action(
        service: Feathers.Service<any>,
        id: number,
        data: object = {}
    ): Promise<CustomError | undefined> {
        try {
            const results = await service.update( id, data );

            if( results !== undefined && results.Error !== undefined )
                return new CustomError( results.Error.Id, results.Error.Message );

        } catch( error ) {
            return ProcessException( error );
        }

        return undefined;
    }
}
